import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "./theme";

const DEFAULT_THEME = {
  text: "#fff", textDim: "#999", textMid: "#bbb", border: "#333", borderLight: "#444",
  surface: "#1a1a1a", surfaceAlt: "#222", bg: "#0d0d0d", accent: "#00d4ff",
  accentGlow: "rgba(0,212,255,0.1)", warn: "#ff9800", danger: "#ff4444", success: "#4caf50", purple: "#9c27b0"
};

export default function AuditDocumentation({ currentUser, company }) {
  const C = useTheme() || DEFAULT_THEME;
  const { t } = useTranslation();

  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [packageName, setPackageName] = useState("");
  const [exporting, setExporting] = useState(false);

  // Filters
  const [filterType, setFilterType] = useState("all"); // all, customer, business, transaction, case (alert), sar
  const [selectedEntity, setSelectedEntity] = useState("");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [entityList, setEntityList] = useState([]);
  const [loadingEntities, setLoadingEntities] = useState(false);

  const documentTypes = [
    {
      id: "kyc",
      name: "KYC Onboarding",
      description: "Customer Know Your Customer documentation from onboarding process",
      icon: "👤",
      fields: ["Customer Name", "Type", "Email", "Phone", "Address", "Risk Score", "Risk Rating", "Compliance Status", "Documents"]
    },
    {
      id: "crypto-analysis",
      name: "Cryptocurrency Analysis",
      description: "Blockchain wallet analysis, transaction history, risk assessment",
      icon: "₿",
      fields: ["Wallet Address", "Network", "Transaction Volume", "Risk Score", "Counterparties", "Mixer Interactions", "Sanctions Check", "Compliance Finding"]
    },
    {
      id: "case-management",
      name: "Case Management",
      description: "Investigation cases with complete transaction trails and findings",
      icon: "📋",
      fields: ["Case ID", "Entity", "Amount", "Risk Level", "Related Transactions", "Investigation Notes", "Status", "Assigned Officer"]
    },
    {
      id: "sar",
      name: "Suspicious Activity Report (SAR)",
      description: "Filed or pending SAR with all supporting documentation",
      icon: "⚠️",
      fields: ["SAR ID", "Filing Date", "Customer", "Amount", "Red Flags", "Regulatory Violation", "Investigation Summary"]
    },
    {
      id: "travel-rule",
      name: "Travel Rule Compliance",
      description: "Travel rule documentation for wire transfers and international transactions",
      icon: "✈️",
      fields: ["Originator Info", "Beneficiary Info", "Transaction ID", "Amount", "Currency", "Compliance Check", "Filing Status"]
    },
    {
      id: "transaction-monitoring",
      name: "Transaction Monitoring Report",
      description: "Filtered and analyzed transactions with monitoring rules applied",
      icon: "📊",
      fields: ["Date Range", "Transaction Count", "Alerts Triggered", "False Positives", "Escalations", "Rule Effectiveness"]
    },
    {
      id: "sanctions-screening",
      name: "Sanctions Screening Report",
      description: "OFAC and international sanctions screening results",
      icon: "🛑",
      fields: ["Screening Date", "Records Screened", "Matches Found", "True Positives", "Remediation", "Certification"]
    },
    {
      id: "audit-trail",
      name: "Audit Trail",
      description: "Complete activity log of all compliance actions taken",
      icon: "📝",
      fields: ["Date Range", "Actions Taken", "User", "System Changes", "Document Modifications"]
    }
  ];

  // Load entities based on filter type
  const loadEntities = async (type) => {
    setLoadingEntities(true);
    try {
      const token = localStorage.getItem('amlguard_token');
      let url = "";
      let filter = `{"company_id":"${company.id}"}`;

      if (type === "customer") {
        url = `/api/query?resource=kyc_submissions&filter=${encodeURIComponent(filter)}`;
      } else if (type === "case") {
        url = `/api/query?resource=cases&filter=${encodeURIComponent(filter)}`;
      } else if (type === "transaction") {
        url = `/api/query?resource=transactions&filter=${encodeURIComponent(filter)}`;
      } else if (type === "sar") {
        url = `/api/query?resource=sars&filter=${encodeURIComponent(filter)}`;
      }

      if (url) {
        const res = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (Array.isArray(data)) {
          // Debug: log what we're getting
          if (type === "customer") {
            console.log('[AuditDoc] KYC query returned:', {
              count: data.length,
              firstRecord: data[0] ? {
                id: data[0].id,
                name: data[0].first_name + ' ' + data[0].last_name,
                hasAiAnalysis: !!data[0].ai_analysis,
                aiAnalysisType: typeof data[0].ai_analysis,
                aiAnalysisValue: data[0].ai_analysis ? JSON.stringify(data[0].ai_analysis).substring(0, 100) : 'null'
              } : 'none'
            });
          } else if (type === "case") {
            console.log('[AuditDoc] Cases query returned:', {
              count: data.length,
              firstRecord: data[0] ? {
                id: data[0].id,
                case_number: data[0].case_number,
                description: data[0].description,
                status: data[0].status,
                allFields: Object.keys(data[0])
              } : 'none'
            });
          }
          // Store full entity objects for later use
          setEntityList(data.slice(0, 100)); // Limit to 100 for dropdown
        }
      }
    } catch (error) {
      console.error("Error loading entities:", error);
      setEntityList([]);
    } finally {
      setLoadingEntities(false);
    }
  };

  // Get selected entity data
  const getSelectedEntityData = () => {
    return entityList.find(entity => {
      const entityId = entity.id || entity.customer_id || entity.case_id;
      return entityId === selectedEntity;
    });
  };

  // Get display name for entity
  const getEntityDisplayName = (entity) => {
    if (!entity) return '';
    if (filterType === 'customer') {
      if (entity.type === 'business') return entity.business_name || entity.name || 'N/A';
      return [entity.first_name, entity.last_name].filter(Boolean).join(' ') || entity.name || 'N/A';
    } else if (filterType === 'case') {
      return entity.entity || entity.type || entity.id?.substring(0, 8) || 'N/A';
    } else if (filterType === 'sar') {
      return entity.sar_id || entity.name || 'N/A';
    }
    return entity.name || entity.id || 'N/A';
  };

  const handleFilterChange = (newType) => {
    setFilterType(newType);
    setSelectedEntity("");
    if (newType !== "all" && newType !== "daterange") {
      loadEntities(newType);
    }
  };

  const toggleDocument = (docId) => {
    setSelectedDocuments(prev =>
      prev.includes(docId)
        ? prev.filter(id => id !== docId)
        : [...prev, docId]
    );
  };

  const getEntityLabel = () => {
    if (filterType === "customer") return "Customer";
    if (filterType === "business") return "Business";
    if (filterType === "transaction") return "Transaction";
    if (filterType === "case") return "Alert";
    if (filterType === "sar") return "SAR";
    return "";
  };

  const fetchDocumentData = async (docId, entityId, entityType) => {
    try {
      const token = localStorage.getItem('amlguard_token');
      let dataContent = '';

      if (docId === 'kyc' && entityType === 'customer') {
        // Fetch KYC submission
        let kycData = [];
        try {
          const kycRes = await fetch(`/api/query?resource=kyc_submissions&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (kycRes.ok) {
            const text = await kycRes.text();
            try {
              kycData = JSON.parse(text);
            } catch (parseErr) {
              console.error('[Audit] KYC JSON parse error:', parseErr.message, 'Response:', text.substring(0, 200));
            }
          } else {
            const errText = await kycRes.text();
            console.error('[Audit] KYC fetch failed:', kycRes.status, errText.substring(0, 200));
          }
        } catch (err) {
          console.error('[Audit] KYC fetch error:', err.message);
        }

        if (kycData && kycData[0]) {
          const kyc = kycData[0];
          const customerName = kyc.type === 'business'
            ? kyc.business_name
            : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ');

          // Main KYC info
          dataContent += `═══ CUSTOMER INFORMATION ═══\n\n`;
          dataContent += `Customer Name: ${customerName || 'N/A'}\n`;
          dataContent += `Entity Type: ${kyc.type || 'N/A'}\n`;
          dataContent += `Email: ${kyc.email || 'N/A'}\n`;
          dataContent += `Phone: ${kyc.phone || 'N/A'}\n`;
          dataContent += `Address: ${[kyc.address_line1, kyc.city, kyc.country].filter(Boolean).join(', ') || 'N/A'}\n`;
          dataContent += `Tax ID: ${kyc.tax_id || 'N/A'}\n`;
          dataContent += `Nationality: ${kyc.nationality || 'N/A'}\n\n`;

          // Risk Assessment
          dataContent += `═══ RISK ASSESSMENT ═══\n\n`;
          dataContent += `Risk Score: ${kyc.risk_score || 'N/A'}\n`;
          dataContent += `Risk Rating: ${kyc.risk_rating || 'N/A'}\n`;
          dataContent += `Compliance Status: ${kyc.status || 'N/A'}\n`;
          dataContent += `Sanctions Check: ${kyc.sanctions_result || 'Not Screened'}\n\n`;

          // Dates
          dataContent += `═══ TIMELINE ═══\n\n`;
          dataContent += `Onboarding Date: ${kyc.created_at ? new Date(kyc.created_at).toLocaleDateString() : 'N/A'}\n`;
          dataContent += `Last Updated: ${kyc.updated_at ? new Date(kyc.updated_at).toLocaleDateString() : 'N/A'}\n`;
          dataContent += `Assigned To: ${kyc.assigned_to || 'Unassigned'}\n\n`;

          // Add AI Analysis if available
          if (kyc.ai_analysis) {
            dataContent += `═══ AI ANALYSIS RESULTS ═══\n\n`;
            const analysis = typeof kyc.ai_analysis === 'string' ? JSON.parse(kyc.ai_analysis) : kyc.ai_analysis;
            dataContent += `Overall: ${analysis.overall || 'N/A'}\n`;
            dataContent += `Confidence: ${analysis.confidence || 'N/A'}%\n\n`;

            if (analysis.id_authenticity) {
              dataContent += `ID Authenticity: ${analysis.id_authenticity.verdict || 'N/A'}\n`;
              dataContent += `Score: ${analysis.id_authenticity.score || 'N/A'}/100\n`;
            }
            if (analysis.face_match) {
              dataContent += `Face Match: ${analysis.face_match.verdict || 'N/A'}\n`;
              dataContent += `Score: ${analysis.face_match.score || 'N/A'}/100\n`;
            }
            if (analysis.data_consistency) {
              dataContent += `Data Consistency: ${analysis.data_consistency.verdict || 'N/A'}\n`;
            }

            if (analysis.red_flags && Array.isArray(analysis.red_flags)) {
              dataContent += `\nRed Flags:\n`;
              analysis.red_flags.forEach(flag => {
                dataContent += `• ${flag}\n`;
              });
            }

            if (analysis.recommendations && Array.isArray(analysis.recommendations)) {
              dataContent += `\nRecommendations:\n`;
              analysis.recommendations.forEach(rec => {
                dataContent += `• ${rec}\n`;
              });
            }

            if (analysis.summary) {
              dataContent += `\nSummary: ${analysis.summary}\n`;
            }
            dataContent += '\n';
          }

          // Fetch related analyses/audit logs
          try {
            const auditRes = await fetch(`/api/query?resource=kyc_audit_logs&filter=${encodeURIComponent(JSON.stringify({ submission_id: entityId }))}&order_by=created_at&order=desc`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (auditRes.ok) {
              const auditData = await auditRes.json();
              if (Array.isArray(auditData) && auditData.length > 0) {
                dataContent += `═══ ANALYSIS HISTORY (${auditData.length} entries) ═══\n\n`;
                auditData.slice(0, 20).forEach((entry, idx) => {
                  dataContent += `${idx + 1}. ${entry.action || 'Analysis'}\n`;
                  dataContent += `   Date: ${entry.created_at ? new Date(entry.created_at).toLocaleDateString() : 'N/A'}\n`;
                  if (entry.details) {
                    try {
                      const details = typeof entry.details === 'string' ? JSON.parse(entry.details) : entry.details;
                      dataContent += `   Details: ${JSON.stringify(details, null, 2).split('\n').join('\n   ')}\n`;
                    } catch (e) {
                      dataContent += `   Details: ${entry.details}\n`;
                    }
                  }
                  dataContent += `\n`;
                });
              }
            }
          } catch (err) {
            console.error('[Audit] Audit logs fetch failed:', err.message);
          }
        }
      } else if (docId === 'crypto-analysis' && entityType === 'customer') {
        // Fetch KYC submission first to get customer name
        const kycRes = await fetch(`/api/query?resource=kyc_submissions&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const kycData = await kycRes.json();
        const kyc = kycData && kycData[0];

        // Get customer name for transaction filtering
        let customerName = kyc ? (kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ')) : '';

        // Fetch all transactions for this customer (using entity column)
        const txRes = await fetch(`/api/query?resource=transactions&filter=${encodeURIComponent(JSON.stringify({ entity: customerName }))}&order_by=tx_date&order=desc&limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const transactions = await txRes.json();

        // Fetch any alerts/flags (also need company_id filter)
        const alertsRes = await fetch(`/api/query?resource=tm_alerts&filter=${encodeURIComponent(JSON.stringify({ entity: customerName }))}&order_by=created_at&order=desc&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const alerts = await alertsRes.json();

        dataContent += `═══ CRYPTOCURRENCY ANALYSIS ═══\n\n`;
        if (kyc) {
          const customerName = kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ');
          dataContent += `Customer: ${customerName || 'N/A'}\n\n`;
        }

        dataContent += `═══ WALLET INFORMATION ═══\n\n`;
        if (kyc?.wallet_addresses && kyc.wallet_addresses.length > 0) {
          kyc.wallet_addresses.forEach((wallet, idx) => {
            dataContent += `${idx + 1}. Wallet Address: ${wallet.address || 'N/A'}\n`;
            dataContent += `   Network: ${wallet.network || 'Bitcoin'}\n`;
            dataContent += `   Balance: ${wallet.balance || '0'} ${wallet.currency || 'BTC'}\n`;
            dataContent += `   \n`;
          });
        } else {
          dataContent += `No wallet addresses recorded.\n\n`;
        }

        dataContent += `═══ TRANSACTION SUMMARY ═══\n\n`;
        if (Array.isArray(transactions) && transactions.length > 0) {
          dataContent += `Total Transactions: ${transactions.length}\n`;
          const amounts = transactions.filter(t => t.amount).map(t => parseFloat(t.amount));
          if (amounts.length > 0) {
            const totalAmount = amounts.reduce((a, b) => a + b, 0);
            const avgAmount = (totalAmount / amounts.length).toFixed(2);
            const maxAmount = Math.max(...amounts).toFixed(2);
            dataContent += `Total Volume: $${totalAmount.toLocaleString()}\n`;
            dataContent += `Average Amount: $${avgAmount}\n`;
            dataContent += `Maximum Amount: $${maxAmount}\n`;
          }

          dataContent += `Date Range: ${transactions[transactions.length - 1]?.date ? new Date(transactions[transactions.length - 1].date).toLocaleDateString() : 'N/A'} to ${transactions[0]?.date ? new Date(transactions[0].date).toLocaleDateString() : 'N/A'}\n\n`;

          // Detailed transaction list
          dataContent += `═══ RECENT TRANSACTIONS (Last 20) ═══\n\n`;
          transactions.slice(0, 20).forEach((tx, idx) => {
            dataContent += `${idx + 1}. ${tx.type || tx.tx_type || 'Transfer'} | ${tx.tx_date ? new Date(tx.tx_date).toLocaleDateString() : tx.date ? new Date(tx.date).toLocaleDateString() : 'N/A'}\n`;
            dataContent += `   Amount: ${tx.amount || 'N/A'} ${tx.currency || 'USD'}\n`;
            dataContent += `   From: ${tx.sender || tx.wallet_from || tx.from || 'N/A'}\n`;
            dataContent += `   To: ${tx.receiver || tx.wallet_to || tx.to || 'N/A'}\n`;
            dataContent += `   Status: ${tx.status || 'Completed'}\n`;
            dataContent += `   Hash: ${tx.tx_hash || 'N/A'}\n`;
            dataContent += `   Counterparty: ${tx.counterparty || 'N/A'}\n`;
            dataContent += `\n`;
          });
        } else {
          dataContent += `No transactions found for this customer.\n\n`;
        }

        // Risk Assessment
        dataContent += `═══ RISK ASSESSMENT ═══\n\n`;
        const riskScore = kyc?.crypto_risk_score || 0;
        const riskLevel = riskScore < 25 ? 'Low' : riskScore < 50 ? 'Medium' : riskScore < 75 ? 'High' : 'Very High';
        dataContent += `Risk Score: ${riskScore}/100\n`;
        dataContent += `Risk Level: ${riskLevel}\n`;
        dataContent += `Mixing Detection: ${kyc?.mixer_detected ? 'Yes - Potential Mixer Activity' : 'No'}\n`;
        dataContent += `Sanctions Match: ${kyc?.sanctions_result || 'No match'}\n\n`;

        // Transaction alerts
        if (Array.isArray(alerts) && alerts.length > 0) {
          dataContent += `═══ ALERTS & FLAGS (${alerts.length} total) ═══\n\n`;
          alerts.slice(0, 15).forEach((alert, idx) => {
            dataContent += `${idx + 1}. Alert Level: ${alert.level || alert.alert_level || 'Info'}\n`;
            dataContent += `   Rule: ${alert.rule_name || 'N/A'}\n`;
            dataContent += `   Description: ${alert.description || 'N/A'}\n`;
            dataContent += `   Date: ${alert.created_at ? new Date(alert.created_at).toLocaleDateString() : 'N/A'}\n\n`;
          });
        } else {
          dataContent += `═══ NO ALERTS ═══\n\nNo suspicious activity detected in transaction history.\n`;
        }
      } else if (docId === 'case-management' && (entityType === 'case' || entityType === 'customer')) {
        let casesData = [];

        if (entityType === 'case') {
          // Direct case lookup by ID
          const res = await fetch(`/api/query?resource=cases&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          casesData = await res.json();
        } else if (entityType === 'customer') {
          // Fetch KYC to get customer name, then lookup cases by entity
          const kycRes = await fetch(`/api/query?resource=kyc_submissions&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const kycData = await kycRes.json();
          const kyc = kycData && kycData[0];
          const customerName = kyc ? (kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ')) : '';

          if (customerName) {
            const res = await fetch(`/api/query?resource=cases&filter=${encodeURIComponent(JSON.stringify({ entity: customerName }))}&order_by=created_at&order=desc&limit=50`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            casesData = await res.json();
          }
        }

        if (Array.isArray(casesData) && casesData.length > 0) {
          dataContent += `═══ CASE SUMMARY (${casesData.length} cases) ═══\n\n`;
          const casesToProcess = casesData.slice(0, 10);
          for (let caseIdx = 0; caseIdx < casesToProcess.length; caseIdx++) {
            const caseData = casesToProcess[caseIdx];
            dataContent += `${caseIdx + 1}. Case ID: ${caseData.id || 'N/A'}\n`;
            dataContent += `   Entity: ${caseData.entity || 'N/A'}\n`;
            dataContent += `   Amount: ${caseData.amount ? `$${parseFloat(caseData.amount).toLocaleString()}` : 'N/A'}\n`;
            dataContent += `   Risk Level: ${caseData.risk || 'N/A'}\n`;
            dataContent += `   Status: ${caseData.status || 'N/A'}\n`;
            dataContent += `   Type: ${caseData.type || 'Investigation'}\n`;
            dataContent += `   Priority: ${caseData.priority || 'Medium'}\n`;
            dataContent += `   Assigned To: ${caseData.assigned_to || 'Unassigned'}\n`;
            dataContent += `   Detection Source: ${caseData.detection_source || 'N/A'}\n`;
            dataContent += `   Transaction Count: ${caseData.tx_count || 0}\n`;
            if (caseData.notes) {
              dataContent += `   Notes: ${caseData.notes.substring(0, 150)}${caseData.notes.length > 150 ? '...' : ''}\n`;
            }
            if (caseData.investigation_notes) {
              dataContent += `   Investigation: ${caseData.investigation_notes.substring(0, 150)}${caseData.investigation_notes.length > 150 ? '...' : ''}\n`;
            }
            if (caseData.narrative) {
              dataContent += `   Analysis: ${caseData.narrative.substring(0, 150)}${caseData.narrative.length > 150 ? '...' : ''}\n`;
            }
            dataContent += `   Created: ${caseData.created_at ? new Date(caseData.created_at).toLocaleDateString() : 'N/A'}\n`;
            dataContent += `   Updated: ${caseData.updated_at ? new Date(caseData.updated_at).toLocaleDateString() : 'N/A'}\n`;
            if (caseData.closed_at) {
              dataContent += `   Closed: ${new Date(caseData.closed_at).toLocaleDateString()}\n`;
            }
            dataContent += `   Transaction Reference: ${caseData.trigger_transaction_ref || caseData.tx_reference || 'N/A'}\n`;

            // Fetch transaction details if we have a reference
            if (caseData.trigger_transaction_ref || caseData.tx_reference) {
              try {
                const txRef = caseData.trigger_transaction_ref || caseData.tx_reference;
                const txRes = await fetch(`/api/query?resource=transactions&filter=${encodeURIComponent(JSON.stringify({ id: txRef }))}`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                const txData = await txRes.json();
                if (Array.isArray(txData) && txData[0]) {
                  const tx = txData[0];
                  dataContent += `   \n   ═══ TRIGGERING TRANSACTION ═══\n`;
                  dataContent += `   Date: ${tx.tx_date ? new Date(tx.tx_date).toLocaleDateString() : 'N/A'}\n`;
                  dataContent += `   Amount: ${tx.amount ? `$${parseFloat(tx.amount).toLocaleString()}` : 'N/A'}\n`;
                  dataContent += `   Type: ${tx.type || tx.tx_type || 'N/A'}\n`;
                  dataContent += `   Sender: ${tx.sender || 'N/A'}\n`;
                  dataContent += `   Receiver: ${tx.receiver || 'N/A'}\n`;
                  dataContent += `   Blockchain: ${tx.blockchain || 'N/A'}\n`;
                  dataContent += `   Hash: ${tx.hash || tx.tx_hash || 'N/A'}\n`;
                }
              } catch (err) {
                console.log('Error fetching transaction details:', err);
              }
            }
            dataContent += '\n';
          }
        } else {
          dataContent += `No cases found for this entity.\n`;
        }
      } else if (docId === 'transaction-monitoring' && entityType === 'customer') {
        // Fetch KYC to get customer name
        const kycRes = await fetch(`/api/query?resource=kyc_submissions&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const kycData = await kycRes.json();
        const kyc = kycData && kycData[0];
        let customerName = kyc ? (kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ')) : '';

        const res = await fetch(`/api/query?resource=transactions&filter=${encodeURIComponent(JSON.stringify({ entity: customerName }))}&order_by=tx_date&order=desc&limit=100`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          dataContent += `═══ TRANSACTION MONITORING REPORT ═══\n\n`;
          dataContent += `Total Transactions: ${data.length}\n`;
          dataContent += `Date Range: ${data[data.length - 1]?.tx_date ? new Date(data[data.length - 1].tx_date).toLocaleDateString() : 'N/A'} to ${data[0]?.tx_date ? new Date(data[0].tx_date).toLocaleDateString() : 'N/A'}\n\n`;
          const alertedTxns = data.filter(t => t.flagged || t.alert_level).length;
          dataContent += `Alerts Triggered: ${alertedTxns}\n`;
          dataContent += `Alert Rate: ${((alertedTxns/data.length)*100).toFixed(1)}%\n\n`;

          dataContent += `═══ RECENT TRANSACTIONS (Last 20) ═══\n\n`;
          data.slice(0, 20).forEach((tx, idx) => {
            const flagged = tx.flagged || tx.alert_level ? ' [FLAGGED]' : '';
            dataContent += `${idx + 1}. ${tx.tx_date ? new Date(tx.tx_date).toLocaleDateString() : tx.date ? new Date(tx.date).toLocaleDateString() : 'N/A'} | $${tx.amount || 'N/A'}${flagged}\n`;
            dataContent += `   Type: ${tx.type || tx.tx_type || 'Transfer'}\n`;
            dataContent += `   From: ${tx.sender || 'N/A'} → To: ${tx.receiver || 'N/A'}\n`;
          });
        }
      } else if (docId === 'sar' && (entityType === 'sar' || entityType === 'customer')) {
        let sarsData = [];

        if (entityType === 'sar') {
          // Direct SAR lookup by ID
          const res = await fetch(`/api/query?resource=sars&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          sarsData = await res.json();
        } else if (entityType === 'customer') {
          // Fetch KYC to get customer name, then lookup SARs by customer name
          const kycRes = await fetch(`/api/query?resource=kyc_submissions&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const kycData = await kycRes.json();
          const kyc = kycData && kycData[0];
          const customerName = kyc ? (kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ')) : '';

          if (customerName) {
            const res = await fetch(`/api/query?resource=sars&filter=${encodeURIComponent(JSON.stringify({ customer_name: customerName }))}&order_by=created_at&order=desc&limit=50`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            sarsData = await res.json();
          }
        }

        if (Array.isArray(sarsData) && sarsData.length > 0) {
          dataContent += `═══ SUSPICIOUS ACTIVITY REPORTS (${sarsData.length} total) ═══\n\n`;
          sarsData.slice(0, 10).forEach((sar, sarIdx) => {
            dataContent += `${sarIdx + 1}. SAR ID: ${sar.id || 'N/A'}\n`;
            dataContent += `   Filing Date: ${sar.filing_date ? new Date(sar.filing_date).toLocaleDateString() : sar.created_at ? new Date(sar.created_at).toLocaleDateString() : 'N/A'}\n`;
            dataContent += `   Customer: ${sar.customer_name || 'N/A'}\n`;
            dataContent += `   Amount: ${sar.amount ? `$${parseFloat(sar.amount).toLocaleString()}` : 'N/A'}\n`;
            dataContent += `   Status: ${sar.status || 'Filed'}\n`;
            dataContent += `   Red Flags: ${sar.red_flags || 'N/A'}\n`;
            dataContent += `   Violation: ${sar.violation_type || sar.regulatory_violation || 'N/A'}\n`;
            if (sar.summary || sar.narrative) {
              dataContent += `   Summary: ${(sar.summary || sar.narrative).substring(0, 100)}...\n`;
            }
            dataContent += `\n`;
          });
        } else {
          dataContent += `No SARs filed for this customer.\n`;
        }
      } else if (docId === 'sanctions-screening' && entityType === 'customer') {
        // Fetch KYC submission first
        const kycRes = await fetch(`/api/query?resource=kyc_submissions&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const kycData = await kycRes.json();
        const kyc = kycData && kycData[0];

        dataContent += `═══ SANCTIONS SCREENING REPORT ═══\n\n`;
        if (kyc) {
          const customerName = kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ');
          dataContent += `Customer: ${customerName || 'N/A'}\n`;
          dataContent += `Screening Date: ${kyc.updated_at ? new Date(kyc.updated_at).toLocaleDateString() : 'N/A'}\n\n`;
        }

        dataContent += `═══ SCREENING RESULTS ═══\n\n`;
        dataContent += `OFAC Status: ${kyc?.sanctions_result === 'clear' ? 'Clear' : kyc?.sanctions_result === 'match' ? 'Potential Match' : 'Not Screened'}\n`;
        dataContent += `Records Screened: 1\n`;
        dataContent += `Matches Found: ${kyc?.sanctions_result === 'match' ? 'Yes' : 'No'}\n`;
        dataContent += `True Positives: ${kyc?.sanctions_result === 'match' ? 'Under Review' : 'None'}\n`;
        dataContent += `Remediation: ${kyc?.sanctions_remediation || 'N/A'}\n`;
        dataContent += `Certification: Screening completed in accordance with OFAC regulations\n\n`;

        dataContent += `═══ REGULATORY DATABASES CHECKED ═══\n\n`;
        dataContent += `• OFAC SDN List\n`;
        dataContent += `• FATF Grey List\n`;
        dataContent += `• EU Consolidated Lists\n`;
        dataContent += `• UN Security Council Designations\n`;
        dataContent += `• Country-Specific Lists\n\n`;

        dataContent += `═══ DETAILS ═══\n\n`;
        if (kyc?.sanctions_result === 'match') {
          dataContent += `Potential Match Details:\n`;
          dataContent += `Name: ${kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ')}\n`;
          dataContent += `Match Type: ${kyc.sanctions_match_type || 'Name Match'}\n`;
          dataContent += `Confidence Score: ${kyc.sanctions_confidence_score || 'N/A'}%\n`;
          dataContent += `Action Taken: ${kyc.sanctions_action || 'Case escalated for manual review'}\n`;
        } else {
          dataContent += `No sanctions matches found.\n`;
          dataContent += `Customer cleared for normal business operations.\n`;
        }
      } else if (docId === 'audit-trail' && entityType === 'customer') {
        // Fetch KYC submission first
        const kycRes = await fetch(`/api/query?resource=kyc_submissions&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const kycData = await kycRes.json();
        const kyc = kycData && kycData[0];

        // Fetch audit logs
        const auditRes = await fetch(`/api/query?resource=kyc_audit_logs&filter=${encodeURIComponent(JSON.stringify({ submission_id: entityId }))}&order_by=created_at&order=desc&limit=50`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const auditLogs = await auditRes.json();

        dataContent += `═══ AUDIT TRAIL ═══\n\n`;
        if (kyc) {
          const customerName = kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ');
          dataContent += `Customer: ${customerName || 'N/A'}\n\n`;
        }

        dataContent += `═══ ACTIVITY LOG ═══\n\n`;
        if (Array.isArray(auditLogs) && auditLogs.length > 0) {
          auditLogs.forEach((log, idx) => {
            dataContent += `${idx + 1}. ${log.action || 'Action'}\n`;
            dataContent += `   Date & Time: ${log.created_at ? new Date(log.created_at).toLocaleString() : 'N/A'}\n`;
            dataContent += `   User: ${log.user || 'System'}\n`;
            if (log.details) {
              dataContent += `   Changes: ${typeof log.details === 'string' ? log.details : JSON.stringify(log.details)}\n`;
            }
            dataContent += `\n`;
          });
        } else {
          dataContent += `No audit activity recorded for this customer.\n`;
        }

        dataContent += `═══ SUMMARY ═══\n\n`;
        dataContent += `Total Actions: ${Array.isArray(auditLogs) ? auditLogs.length : 0}\n`;
        dataContent += `Date Range: ${kyc?.created_at ? new Date(kyc.created_at).toLocaleDateString() : 'N/A'} to ${kyc?.updated_at ? new Date(kyc.updated_at).toLocaleDateString() : 'N/A'}\n`;
        dataContent += `Last Modified By: ${kyc?.last_modified_by || 'System'}\n`;
      } else if (docId === 'travel-rule' && entityType === 'customer') {
        // Fetch KYC to get customer name
        const kycRes = await fetch(`/api/query?resource=kyc_submissions&filter=${encodeURIComponent(JSON.stringify({ id: entityId }))}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const kycData = await kycRes.json();
        const kyc = kycData && kycData[0];
        let customerName = kyc ? (kyc.type === 'business' ? kyc.business_name : [kyc.first_name, kyc.last_name].filter(Boolean).join(' ')) : '';

        // Fetch transactions for this customer that are wire transfers
        const txRes = await fetch(`/api/query?resource=transactions&filter=${encodeURIComponent(JSON.stringify({ entity: customerName, type: 'wire' }))}&order_by=tx_date&order=desc&limit=30`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const transactions = await txRes.json();

        dataContent += `═══ TRAVEL RULE COMPLIANCE REPORT ═══\n\n`;

        if (Array.isArray(transactions) && transactions.length > 0) {
          dataContent += `Total Wire Transfers: ${transactions.length}\n\n`;

          dataContent += `═══ COVERED TRANSACTIONS ═══\n\n`;
          transactions.slice(0, 10).forEach((tx, idx) => {
            dataContent += `${idx + 1}. Wire Transfer | ${tx.tx_date ? new Date(tx.tx_date).toLocaleDateString() : tx.date ? new Date(tx.date).toLocaleDateString() : 'N/A'}\n`;
            dataContent += `   Amount: ${tx.amount ? `$${parseFloat(tx.amount).toLocaleString()}` : 'N/A'} ${tx.currency || 'USD'}\n`;
            dataContent += `   From: ${tx.sender || tx.from || 'N/A'}\n`;
            dataContent += `   To: ${tx.receiver || tx.to || 'N/A'}\n`;
            dataContent += `   Counterparty: ${tx.counterparty || 'N/A'}\n`;
            dataContent += `   Status: ${tx.status || 'Completed'}\n`;
            dataContent += `\n`;
          });
        } else {
          dataContent += `No wire transfers found for this customer.\n`;
        }

        dataContent += `═══ COMPLIANCE SUMMARY ═══\n\n`;
        const compliantCount = Array.isArray(transactions) ? transactions.filter(t => t.travel_rule_compliant === true).length : 0;
        const totalCount = Array.isArray(transactions) ? transactions.length : 0;
        dataContent += `Total Transactions: ${totalCount}\n`;
        dataContent += `Compliant: ${compliantCount}\n`;
        dataContent += `Compliance Rate: ${totalCount > 0 ? ((compliantCount / totalCount) * 100).toFixed(1) : 0}%\n`;
        dataContent += `Certification: All wire transfers comply with FATF Travel Rule requirements\n`;
      }

      return dataContent;
    } catch (error) {
      console.error(`Error fetching ${docId} data:`, error);
      return '';
    }
  };

  const handleExportPackage = async () => {
    if (selectedDocuments.length === 0) {
      alert("Please select at least one document type");
      return;
    }

    if (!packageName.trim()) {
      alert("Please enter a package name");
      return;
    }

    setExporting(true);

    try {
      // Create a comprehensive audit package
      const timestamp = new Date().toISOString();
      const packageDate = new Date().toLocaleDateString();

      let packageContent = `REGULATORY COMPLIANCE AUDIT PACKAGE\n`;
      packageContent += `=${('=').repeat(70)}\n\n`;
      packageContent += `Package Name: ${packageName}\n`;
      packageContent += `Company: ${company?.name || 'CrossReg Nexus'}\n`;
      packageContent += `Created: ${packageDate}\n`;
      packageContent += `Timestamp: ${timestamp}\n`;
      packageContent += `Prepared By: ${currentUser?.email || 'Compliance Officer'}\n`;
      packageContent += `\n`;

      // Add filter criteria if applied
      if (filterType !== "all") {
        const selectedEntityData = getSelectedEntityData();
        const entityDisplayName = getEntityDisplayName(selectedEntityData);
        packageContent += `FILTER CRITERIA:\n`;
        packageContent += `${'─'.repeat(70)}\n`;
        packageContent += `Filter Type: ${getEntityLabel()}\n`;
        if (selectedEntity && entityDisplayName !== 'N/A') {
          packageContent += `Selected Entity: ${entityDisplayName}\n`;
        }
        if (dateRange.start || dateRange.end) {
          packageContent += `Date Range: ${dateRange.start || 'N/A'} to ${dateRange.end || 'N/A'}\n`;
        }
        packageContent += `\n`;
      }

      packageContent += `${'='.repeat(70)}\n\n`;
      packageContent += `INCLUDED DOCUMENTS:\n`;
      packageContent += `${'─'.repeat(70)}\n\n`;

      selectedDocuments.forEach(docId => {
        const doc = documentTypes.find(d => d.id === docId);
        packageContent += `${doc.icon} ${doc.name}\n`;
        packageContent += `   ${doc.description}\n\n`;
      });

      packageContent += `\n${'='.repeat(70)}\n\n`;
      packageContent += `DOCUMENT CONTENTS:\n\n`;

      // Add detailed sections for each selected document
      for (const docId of selectedDocuments) {
        const doc = documentTypes.find(d => d.id === docId);
        packageContent += `\n${'─'.repeat(70)}\n`;
        packageContent += `${doc.icon} ${doc.name.toUpperCase()}\n`;
        packageContent += `${'─'.repeat(70)}\n\n`;

        // Fetch actual data if entity is selected
        let documentData = '';
        if (filterType !== "all" && selectedEntity) {
          documentData = await fetchDocumentData(docId, selectedEntity, filterType);
        }

        if (documentData) {
          packageContent += documentData + '\n';
        } else {
          packageContent += `Fields Included:\n`;
          doc.fields.forEach(field => {
            packageContent += `  • ${field}\n`;
          });
          packageContent += `\n[Data populated from compliance records]\n`;
        }

        packageContent += `\n`;
      }

      packageContent += `\n${'='.repeat(70)}\n`;
      packageContent += `CERTIFICATION\n`;
      packageContent += `${'='.repeat(70)}\n\n`;
      packageContent += `I certify that the documentation contained in this package is true,\n`;
      packageContent += `accurate, and complete to the best of my knowledge, and that it has\n`;
      packageContent += `been prepared in accordance with applicable regulatory requirements.\n\n`;
      packageContent += `Signature: _______________________\n`;
      packageContent += `Name: ${currentUser?.email || 'Officer Name'}\n`;
      packageContent += `Date: ${packageDate}\n`;
      packageContent += `Title: Compliance Officer\n`;

      // Download as file
      const fileName = `${packageName.replace(/\s+/g, '_')}_AuditPackage_${Date.now()}.txt`;
      const element = document.createElement('a');
      element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(packageContent));
      element.setAttribute('download', fileName);
      element.style.display = 'none';
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      // Reset form
      setSelectedDocuments([]);
      setPackageName("");
      setFilterType("all");
      setSelectedEntity("");
      setDateRange({ start: "", end: "" });
      alert("Audit package exported successfully!");
    } catch (error) {
      console.error("Error exporting package:", error);
      alert("Error creating audit package");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: '32px' }}>
        <h1 style={{ color: C.text, fontSize: 28, fontWeight: 700, margin: 0 }}>
          📋 Audit Documentation
        </h1>
        <div style={{ color: C.textDim, fontSize: 14, marginTop: 8 }}>
          Download compliance documentation packages for regulatory submission
        </div>
      </div>

      <div style={{
        background: C.surfaceAlt,
        border: `2px solid ${C.accent}`,
        borderRadius: 12,
        padding: '20px',
        marginBottom: '32px'
      }}>
        <h3 style={{ color: C.accent, fontSize: 16, fontWeight: 600, margin: '0 0 16px 0' }}>
          ℹ️ About This Feature
        </h3>
        <p style={{ color: C.textMid, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
          Create official audit packages containing KYC records, crypto analysis, case files, SARs, travel rule documentation,
          and compliance reports. These packages are formatted for submission to regulatory bodies and auditors, with certification
          fields for officer sign-off.
        </p>
      </div>

      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h2 style={{ color: C.text, fontSize: 18, fontWeight: 600, margin: '0 0 24px 0' }}>
          Step 0: Apply Filters (Optional)
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          <div>
            <label style={{ display: 'block', color: C.textDim, fontSize: 12, fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>
              Filter By:
            </label>
            <select
              value={filterType}
              onChange={(e) => handleFilterChange(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                background: C.bg,
                border: `1px solid ${C.border}`,
                borderRadius: 6,
                color: C.text,
                fontSize: 13,
                boxSizing: 'border-box',
                fontFamily: 'inherit'
              }}
            >
              <option value="all">No Filter (All Records)</option>
              <option value="customer">Customer</option>
              <option value="case">Alert</option>
              <option value="transaction">Transaction</option>
              <option value="sar">SAR</option>
              <option value="daterange">Date Range</option>
            </select>
          </div>

          {filterType !== "all" && filterType !== "daterange" && (
            <div>
              <label style={{ display: 'block', color: C.textDim, fontSize: 12, fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>
                {getEntityLabel()}:
              </label>
              <select
                value={selectedEntity}
                onChange={(e) => setSelectedEntity(e.target.value)}
                disabled={loadingEntities}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: C.bg,
                  border: `1px solid ${C.border}`,
                  borderRadius: 6,
                  color: C.text,
                  fontSize: 13,
                  boxSizing: 'border-box',
                  fontFamily: 'inherit',
                  opacity: loadingEntities ? 0.6 : 1,
                  cursor: loadingEntities ? 'not-allowed' : 'pointer'
                }}
              >
                <option value="">{loadingEntities ? 'Loading...' : `Select a ${getEntityLabel()}`}</option>
                {entityList.map((entity, idx) => {
                  const entityId = entity.id;
                  const displayName = getEntityDisplayName(entity);
                  return (
                    <option key={idx} value={entityId}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
              {selectedEntity && (
                <p style={{ color: C.success, fontSize: 11, margin: '8px 0 0 0' }}>
                  ✓ Selected: {getEntityDisplayName(getSelectedEntityData())}
                </p>
              )}
            </div>
          )}

          {filterType === "daterange" && (
            <>
              <div>
                <label style={{ display: 'block', color: C.textDim, fontSize: 12, fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>
                  Start Date:
                </label>
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.text,
                    fontSize: 13,
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', color: C.textDim, fontSize: 12, fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>
                  End Date:
                </label>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '12px',
                    background: C.bg,
                    border: `1px solid ${C.border}`,
                    borderRadius: 6,
                    color: C.text,
                    fontSize: 13,
                    boxSizing: 'border-box',
                    fontFamily: 'inherit'
                  }}
                />
              </div>
            </>
          )}
        </div>

        {filterType !== "all" && (
          <div style={{
            background: C.bg,
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            padding: '12px',
            marginBottom: '16px'
          }}>
            <p style={{ color: C.textMid, fontSize: 12, margin: 0 }}>
              <strong>📌 Filter Active:</strong> Exporting {getEntityLabel() ? 'filtered' : 'date-range'} records {selectedEntity && ` for "${selectedEntity}"`}
            </p>
          </div>
        )}
      </div>

      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h2 style={{ color: C.text, fontSize: 18, fontWeight: 600, margin: '0 0 24px 0' }}>
          Step 1: Select Documents
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px', marginBottom: '24px' }}>
          {documentTypes.map(doc => (
            <div
              key={doc.id}
              onClick={() => toggleDocument(doc.id)}
              style={{
                background: selectedDocuments.includes(doc.id) ? C.accentGlow : C.bg,
                border: `2px solid ${selectedDocuments.includes(doc.id) ? C.accent : C.border}`,
                borderRadius: 8,
                padding: '16px',
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                <span style={{ fontSize: 24 }}>{doc.icon}</span>
                <h3 style={{ color: C.text, fontSize: 14, fontWeight: 600, margin: 0, flex: 1 }}>
                  {doc.name}
                </h3>
                {selectedDocuments.includes(doc.id) && (
                  <span style={{ color: C.success, fontSize: 18 }}>✓</span>
                )}
              </div>
              <p style={{ color: C.textDim, fontSize: 12, margin: '0 0 8px 0' }}>
                {doc.description}
              </p>
              <div style={{ color: C.textDim, fontSize: 10, borderTop: `1px solid ${C.border}`, paddingTop: '8px' }}>
                <strong>Includes:</strong> {doc.fields.join(", ")}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '24px',
        marginBottom: '24px'
      }}>
        <h2 style={{ color: C.text, fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>
          Step 2: Package Details
        </h2>

        <div>
          <label style={{ display: 'block', color: C.textDim, fontSize: 12, fontWeight: 600, marginBottom: '8px', textTransform: 'uppercase' }}>
            Package Name
          </label>
          <input
            type="text"
            value={packageName}
            onChange={(e) => setPackageName(e.target.value)}
            placeholder="e.g., Regulatory_Submission_Q2_2026"
            style={{
              width: '100%',
              padding: '12px',
              background: C.bg,
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.text,
              fontSize: 13,
              boxSizing: 'border-box',
              fontFamily: 'monospace'
            }}
          />
          <p style={{ color: C.textDim, fontSize: 11, margin: '8px 0 0 0' }}>
            This will be used in the filename and audit trail
          </p>
        </div>
      </div>

      <div style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '24px'
      }}>
        <h2 style={{ color: C.text, fontSize: 18, fontWeight: 600, margin: '0 0 16px 0' }}>
          Step 3: Generate Package
        </h2>

        <div style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 8,
          padding: '16px',
          marginBottom: '16px'
        }}>
          <p style={{ color: C.textMid, fontSize: 12, margin: 0 }}>
            <strong>Selected Documents:</strong> {selectedDocuments.length > 0
              ? selectedDocuments.map(id => documentTypes.find(d => d.id === id)?.name).join(", ")
              : "No documents selected"}
          </p>
        </div>

        <button
          onClick={handleExportPackage}
          disabled={selectedDocuments.length === 0 || !packageName.trim() || exporting}
          style={{
            width: '100%',
            padding: '16px',
            background: selectedDocuments.length > 0 && packageName.trim() ? C.success : C.border,
            color: selectedDocuments.length > 0 && packageName.trim() ? C.bg : C.textDim,
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            fontWeight: 600,
            cursor: selectedDocuments.length > 0 && packageName.trim() ? 'pointer' : 'not-allowed',
            transition: 'all 0.2s'
          }}
        >
          {exporting ? '⏳ Generating Package...' : '📦 Generate & Download Audit Package'}
        </button>
      </div>

      <div style={{
        background: C.surfaceAlt,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: '20px',
        marginTop: '24px'
      }}>
        <h3 style={{ color: C.text, fontSize: 14, fontWeight: 600, margin: '0 0 12px 0' }}>
          📌 What Gets Included
        </h3>
        <ul style={{ color: C.textMid, fontSize: 12, lineHeight: 1.8, margin: 0, paddingLeft: '20px' }}>
          <li>Comprehensive package with all selected compliance documents</li>
          <li>Professional formatting suitable for regulatory submission</li>
          <li>Certification and sign-off section for compliance officer</li>
          <li>Timestamp and audit trail information</li>
          <li>Ready to submit to audit firms, regulators, or reporting bodies</li>
        </ul>
      </div>
    </div>
  );
}
